import { SETTINGS_SECTION } from './constants'
import { SETTINGS_MEMORY_ROUTE_TAB_CONFIG } from './settings_modal_memory_tabs'
import {
  INVITATIONS_NON_LEGACY_SUBPATHS,
  LEGACY_ADMIN_EXACT_SEGMENTS,
  LEGACY_ADMIN_PREFIX_SEGMENTS,
} from './settingsConfig'

/**
 * Legacy `/app/admin/users|invitations|migrations|user_groups|permissions` → consolidated People modal.
 * @param {string} pathname
 * @param {string} url — `/app/admin`
 * @returns {{ section: string, settingsTab: string | null } | null}
 */
const matchLegacyAdminPeoplePaths = (pathname, url) => {
  const usersBase = `${url}/users`
  if (pathname === usersBase || pathname.startsWith(`${usersBase}/`)) {
    return { section: SETTINGS_SECTION.adminPeople, settingsTab: 'active_users' }
  }

  const invitationsBase = `${url}/invitations`
  if (pathname === invitationsBase || pathname.startsWith(`${invitationsBase}/`)) {
    const rest = pathname.slice(invitationsBase.length + 1)
    const firstSegment = rest.split('/').filter(Boolean)[0] ?? null
    if (firstSegment != null && INVITATIONS_NON_LEGACY_SUBPATHS.has(firstSegment)) {
      return null
    }
    return { section: SETTINGS_SECTION.adminPeople, settingsTab: 'pending_users' }
  }

  const migrationsBase = `${url}/migrations`
  if (pathname === migrationsBase || pathname.startsWith(`${migrationsBase}/`)) {
    return { section: SETTINGS_SECTION.adminPeople, settingsTab: 'migrating_users' }
  }

  const userGroupsBase = `${url}/user_groups`
  if (pathname === userGroupsBase || pathname.startsWith(`${userGroupsBase}/`)) {
    return { section: SETTINGS_SECTION.adminPeople, settingsTab: 'groups' }
  }

  const permissionsBase = `${url}/permissions`
  if (pathname === permissionsBase || pathname.startsWith(`${permissionsBase}/`)) {
    const rest = pathname.slice(permissionsBase.length + 1)
    const firstSegment = rest.split('/').filter(Boolean)[0] ?? null
    if (firstSegment === 'workflows') {
      return { section: SETTINGS_SECTION.adminPeople, settingsTab: 'workflows' }
    }
    if (firstSegment === 'invitations') {
      return { section: SETTINGS_SECTION.adminPeople, settingsTab: 'invitations' }
    }
    if (firstSegment === 'payments') {
      return { section: SETTINGS_SECTION.adminPeople, settingsTab: 'payments' }
    }
    return { section: SETTINGS_SECTION.adminPeople, settingsTab: 'event_types' }
  }

  return null
}

/**
 * When `ap_rebrand_settings_ia` is on, deep links to legacy admin routes redirect to home + `?settings=…`
 * (and optional `settings_tab=` for memory-routed tabs such as login/permissions/security).
 * @param {string} pathname — `location.pathname`
 * @param {string} url — admin route prefix, e.g. `/app/admin` from `useRouteMatch()`
 * @returns {{ section: string, settingsTab: string | null } | null}
 */
export function matchLegacyAdminPathToSettingsSection(pathname, url) {
  const peopleMatch = matchLegacyAdminPeoplePaths(pathname, url)
  if (peopleMatch != null) {
    return peopleMatch
  }

  const integrationsBase = `${url}/integrations`
  if (pathname === integrationsBase || pathname.startsWith(`${integrationsBase}/`)) {
    return { section: SETTINGS_SECTION.adminAccess, settingsTab: 'integrations' }
  }

  for (const [segment, section] of LEGACY_ADMIN_EXACT_SEGMENTS) {
    const base = `${url}/${segment}`
    if (pathname === base) {
      return { section, settingsTab: null }
    }
    if (pathname.startsWith(`${base}/`)) {
      const rest = pathname.slice(base.length + 1)
      const firstSegment = rest.split('/').filter(Boolean)[0] ?? null
      const isInvitationsStandaloneRoute = (
        segment === 'invitations'
        && firstSegment != null
        && INVITATIONS_NON_LEGACY_SUBPATHS.has(firstSegment)
      )
      if (!isInvitationsStandaloneRoute) {
        const tabConfig = SETTINGS_MEMORY_ROUTE_TAB_CONFIG[section]
        const settingsTab = tabConfig && firstSegment ? firstSegment : null
        return { section, settingsTab }
      }
    }
  }
  for (const [segment, section] of LEGACY_ADMIN_PREFIX_SEGMENTS) {
    const prefix = `${url}/${segment}`
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return {
        section,
        settingsTab: segment === 'notetaker' ? 'notetaker_access' : null,
      }
    }
  }
  return null
}
